import React, {useState, useEffect} from 'react';
import axios 			from 'axios';

import Table 			from '@mui/joy/Table';
import Stack 			from '@mui/joy/Stack';
import Sheet 			from '@mui/joy/Sheet';
import Alert 			from '@mui/joy/Alert';
import IconButton 		from '@mui/joy/IconButton';
import CircularProgress from '@mui/joy/CircularProgress';
import Tooltip 			from '@mui/joy/Tooltip';
import {stoplossType} 	from "./stopData.js"

import { useTheme } 	from '@mui/material/styles';
import useMediaQuery 	from '@mui/material/useMediaQuery';

import IcoDeleteOutline	from '@mui/icons-material/DeleteOutline';
import IcoSnooze 		from '@mui/icons-material/Snooze';
import IcoAlarmOn		from '@mui/icons-material/AlarmOn';
import IcoLock 			from '@mui/icons-material/LockOutlined';
import IcoTrend	 		from '@mui/icons-material/TrendingUp';
import IcoPercent 		from '@mui/icons-material/Percent';
import IcoQuote 		from '@mui/icons-material/PriceChangeOutlined';
import IcoATR 			from '@mui/icons-material/Insights';
import IcoStrength		from '@mui/icons-material/RocketLaunchOutlined';
import IcoMissing	 	from '@mui/icons-material/NotListedLocation';
import IcoAlarm		 	from '@mui/icons-material/AccessTime';

import {config} from "./config.js";

var _uid;
var _locale = '';


function ShowStops () {	
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
	const theme = useTheme();	
	const isMatch = useMediaQuery(theme.breakpoints.down('md'));

	useEffect(() => {
		_uid = localStorage.getItem("username");
		_locale = localStorage.getItem("locale");

		if (_locale == null)
			_locale = navigator.language;
		
		var url = config.IP + "/stops/" + _uid;		
		
		const getStops = async () => {
			
			setLoading(true);
			
			try {
				const response = await axios.get(url)
					.then((res) => {
						setData(res.data);
					});
			} catch (error) {
				setData("Can´t fetch data from the server. Try again.");
				console.error("Get stops failed:", error);
			}
			setLoading(false);
		}		
		
		getStops();
		
	}, []);


	function deleteStop(e, key, i) {
	    var url = config.IP + "/stop/" + key;

		axios.delete(url, { crossdomain: true })
		.then((response) => {
			console.log("Raderat stop", key);
			var array = [...data];
			if (i !== -1) {
				array.splice(i, 1);
				setData(array);
			}
		})
        .catch(error => {
            console.error('Error in deleteStop', error);
        });	    		
	}
	
	
	function getStopIcon(row) {
		switch(row.stoplossType) {
			case stoplossType.Percent.id:
				return <IcoPercent style={{ color: row.alarm ? "grey" : stoplossType.Percent.color}}/>;

			case stoplossType.Quote.id:
				return <IcoQuote style={{ color: row.alarm ? "grey" : stoplossType.Quote.color}}/>;

			case stoplossType.SaveProfit.id:
				return <IcoLock style={{ color: row.alarm ? "grey" : stoplossType.SaveProfit.color}}/>;
				
			case stoplossType.SMA20.id:
				return <IcoTrend style={{ color: row.alarm ? "grey" : stoplossType.SMA20.color}}/>;

			case stoplossType.SMA50.id:
				return <IcoTrend style={{ color: row.alarm ? "grey" : stoplossType.SMA50.color}}/>;

			case stoplossType.ATR.id:
				return <IcoATR style={{ color: row.alarm ? "grey" : stoplossType.ATR.color}}/>;
				
			case stoplossType.Strength.id:
				return <IcoStrength style={{ color: row.alarm ? "grey" : stoplossType.Strength.color}}/>;				

			case stoplossType.Sleep.id:
				return <IcoSnooze style={{ color: row.alarm ? "grey" : stoplossType.Sleep.color}}/>;				
				
			default:
				return <IcoMissing />;
		}	
	}


	function getStatusIcon(alarm, triggered) {	
		if (alarm) {
			return <Tooltip title={getFormattedDateTime(triggered)} variant="solid" placement="top"><IcoAlarmOn color='danger'/></Tooltip>	
		}
		else
			return <IcoAlarm color='success'/>
	}
	
	
	function getFormattedDate(d) {
		var date = new Date(d);
	    var options = {
	        year: "numeric",
	        month: "2-digit",
	        day: "2-digit"
	    };
	    
	    if (isMatch)
	    	options.year = "2-digit";

		return date.toLocaleDateString(_locale, options);
	}
	
	function getFormattedDateTime(d) {
		var date = new Date(d);
	    var options = {
	        year: "numeric",
	        month: "2-digit",
	        day: "2-digit"
	    };
	    
	    if (isMatch)
	    	options.year = "2-digit";

		return date.toLocaleDateString(_locale, options) + " " + date.toLocaleTimeString(_locale, { hour: "2-digit", minute: "2-digit" });
	}
	

	function getFormattedTrigger(p, type) {
		var n;
		
		if (type == stoplossType.SMA20.id)
			return "SMA20";

		if (type == stoplossType.SMA50.id)
			return "SMA50";

		if (type == stoplossType.ATR.id)
			return p.ATRMultiple + "*ATR14";
			
		if (type == stoplossType.Quote.id)
			return Intl.NumberFormat(_locale, {minimumFractionDigits: 2 }).format(p.stoplossPrice);
			
		if (type == stoplossType.Strength.id) {
			var perc = Intl.NumberFormat(_locale, {minimumFractionDigits: 2 }).format(p.percent); 
			
			// If ending with '.00', cut to minimize length
			if (perc.endsWith('00'))
				perc = perc.slice(0, -3);
			
			return  perc + "%>" + p.sma;					
		}

		if (p === null)
			return "-";
			
		n = new Intl.NumberFormat(_locale, {minimumFractionDigits: 2 }).format(p.percent);			

		// If ending with '.00', cut to minimize length
		if (n.endsWith('00'))
			n = n.slice(0, -3);
			
		return n + "%";
	}
	
	
	function getTextColor(alarm) {
		if (alarm)
			return config.triggeredText;
		else
			return 'success';
	}
	
	function getTableSize() {
		if (isMatch)
			return 'sm';
		else
			return 'lg';
	}
    

	return (
		<React.Fragment>
			{loading ? (
				<Stack direction="row" justifyContent="center">
					<CircularProgress variant="plain" size='lg'/>
				</Stack>			
			) : Array.isArray(data) ? (
				<Sheet sx={{ m: 2 , backgroundColor:config.tableColor}}>
					<Table color='success' size={getTableSize()} noWrap>
						<thead>
							{isMatch ? (
							<tr>
								<th style={{width:'8%'}}></th>
								<th style={{width:'28%'}}>Stock</th>
								<th style={{width:'25%'}}>Created</th>
								<th style={{width:'22%', textAlign:'right'}}>Trigger</th>
								<th style={{width:'7%', textAlign:'center'}}></th>							
								<th style={{width:'10%'}}></th>
							</tr>) 
							: (
							<tr>
								<th style={{width:'7%'}}>Stop</th>
								<th style={{width:'52%'}}>Stock</th>
								<th style={{width:'15%'}}>Created</th>
								<th style={{width:'10%', textAlign:'right'}}>Trigger</th>
								<th style={{width:'10%', textAlign:'center'}}>Status</th>							
								<th style={{width:'6%'}}></th>
							</tr>)}
						</thead>			
						
						<tbody>
							{data.map((row, index) => (
								<tr key={row.id}>
									<td>{getStopIcon(row)}</td>
									<td style={{color: getTextColor(row.alarm)}}>{isMatch? row.ticker : row.companyName+' ('+row.ticker+')'}</td>							
									<td style={{color: getTextColor(row.alarm)}}>{getFormattedDate(row.created)}</td>
									<td style={{textAlign:'right', color: getTextColor(row.alarm)}}>{getFormattedTrigger(row, row.stoplossType)}</td>
									<td style={{textAlign:'center'}}>{getStatusIcon(row.alarm, row.triggered)}</td>
									<td><Tooltip title="Delete" variant="solid" placement="top"><IconButton size="lg" variant="plain" color="neutral" onClick={event => deleteStop(event, row.id, index)}><IcoDeleteOutline style={{color: '#7D1212'}}/></IconButton></Tooltip></td>
								</tr>
							))}
						</tbody>
					</Table>				
				</Sheet>
	      ) : <Stack direction="row" justifyContent="center" size="lg"><Alert color="danger" variant="solid">{data}</Alert></Stack>}
		</React.Fragment>
	);	
}

export default ShowStops;